import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
} from "react-router-dom";
import Login from "./components/login/Login";
import Header from "./components/layout/header/Header";
import SidebarLeft from "./components/layout/sidebarLeft/SidebarLeft";
import WorkspaceTabsBar from "./components/layout/tabs/WorkspaceTabsBar";
import {
  WorkspaceTabsProvider,
  useWorkspaceTabs,
} from "./components/layout/tabs/WorkspaceTabsContext";
import Dashboard from "./pages/dashboard/Dashboard";
import ThemeTest from "./components/theme/ThemeTest";
import PrivateRoute from "./components/privateRoute/PrivateRoute";
import ThemeProvider from "./components/theme/ThemeProvider";
import DynamicFavicon from "./components/common/DynamicFavicon";
import Profile from "./pages/profile/Profile";
import Tenant from "./pages/master/tenant/Tenant";
import TenantUsers from "./pages/tenant/TenantUsers";
import TenantDataAreas from "./pages/tenant/TenantDataAreas";
import RolePermissionsPage from "./pages/system/roles/RolePermissionsPage";
import MessagingPage from "./pages/system/messaging/MessagingPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { isAvailableInDeployment } from "./config/deploymentMode";

function loadDynamicRoutes() {
  const context = require.context("./routes", true, /\.route\.js$/);
  return context.keys().map((key) => context(key).default).filter(isAvailableInDeployment);
}

const STATIC_WORKSPACE_ROUTES = [
  { path: "/dashboard", component: Dashboard },
  { path: "/theme-test", component: ThemeTest },
  { path: "/profile", component: Profile, permission: "profile.read" },
  { path: "/tenants", component: Tenant },
  { path: "/tenant-users", component: TenantUsers, permission: "system.users.read" },
  { path: "/tenant-dataareas", component: TenantDataAreas, permission: "system.dataareas.read" },
  { path: "/roles", component: RolePermissionsPage, permission: "system.roles.read" },
  { path: "/messages", component: MessagingPage, permission: "messaging.read" },
];

function App() {
  const dynamicRoutes = useMemo(() => loadDynamicRoutes(), []);
  const workspaceRoutes = useMemo(
    () =>
      [...STATIC_WORKSPACE_ROUTES, ...dynamicRoutes].map((route) => ({
        path: route.path,
        component: route.component,
        icon: route.icon,
        permission: route.permission,
      })),
    [dynamicRoutes],
  );

  return (
    <>
      <Router>
        <AuthProvider>
          <DynamicFavicon />
          <WorkspaceTabsProvider>
            <Routes>
              <Route
                path="/"
                element={
                  <ThemeProvider>
                    <Login />
                  </ThemeProvider>
                }
              />
              <Route
                path="/*"
                element={
                  <PrivateRoute
                    element={<MainLayout workspaceRoutes={workspaceRoutes} />}
                  />
                }
              />
            </Routes>
          </WorkspaceTabsProvider>
        </AuthProvider>
      </Router>
    </>
  );
}

const NotFoundWorkspacePage = () => <Navigate to="/dashboard" replace />;

const WorkspaceRouteRenderer = ({ path, workspaceRoutes, fallback }) => {
  const location = useLocation();
  const forcedLocation = useMemo(
    () => ({
      ...location,
      pathname: path,
    }),
    [location, path],
  );

  return (
    <Routes location={forcedLocation}>
      {workspaceRoutes.map((route) => {
        const PageComponent = route.component;
        return (
          <Route
            key={`workspace-route:${route.path}`}
            path={route.path}
            element={<PageComponent />}
          />
        );
      })}
      <Route path="*" element={fallback} />
    </Routes>
  );
};

const MainLayout = ({ workspaceRoutes }) => {
  const location = useLocation();
  const { tabs, activePath, getRenderKeyForPath, openTab } = useWorkspaceTabs();
  const [viewCache, setViewCache] = useState({});
  const { hasPermission } = useAuth();
  const authorizedRoutes = useMemo(
    () => workspaceRoutes.filter((route) => hasPermission(route.permission)),
    [hasPermission, workspaceRoutes],
  );

  const resolveComponentForPath = useCallback((path) => {
    const matchedRoute = authorizedRoutes.find((route) =>
      matchPath({ path: route.path, end: true }, path),
    );
    return matchedRoute?.component || NotFoundWorkspacePage;
  }, [authorizedRoutes]);

  const resolveRouteMetaForPath = useCallback((path) => {
    return authorizedRoutes.find((route) =>
      matchPath({ path: route.path, end: true }, path),
    ) || null;
  }, [authorizedRoutes]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (!currentPath || currentPath === "/") {
      return;
    }

    const alreadyOpen = tabs.some((tab) => tab.path === currentPath);
    const matchedMeta = resolveRouteMetaForPath(currentPath);
    if (!alreadyOpen || activePath !== currentPath) {
      openTab(currentPath, undefined, matchedMeta?.icon);
    }

    setViewCache((prev) => {
      if (prev[currentPath]) {
        return prev;
      }
      const Component = resolveComponentForPath(currentPath);
      return {
        ...prev,
        [currentPath]: Component,
      };
    });
  }, [activePath, location.pathname, openTab, resolveComponentForPath, resolveRouteMetaForPath, tabs]);

  const tabPaths = tabs.map((tab) => tab.path);
  const renderPaths = tabPaths.includes(activePath)
    ? tabPaths
    : [...tabPaths, activePath].filter(Boolean);

  return (
    <ThemeProvider>
      <section className="body">
        <Header />
        <div className="inner-wrapper">
          <SidebarLeft />
          <section role="main" className="content-body">
            <WorkspaceTabsBar />
            <div className="workspace-page-container">
              {renderPaths.map((path) => {
                const Component = viewCache[path] || resolveComponentForPath(path);
                const isVisible = path === activePath;
                const refreshKey = getRenderKeyForPath(path);

                return (
                  <div
                    key={`tab-view:${path}`}
                    style={{ display: isVisible ? "block" : "none" }}
                  >
                    <WorkspaceRouteRenderer
                      key={`tab-refresh:${path}:${refreshKey}`}
                      path={path}
                      workspaceRoutes={authorizedRoutes}
                      fallback={<Component />}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </section>
    </ThemeProvider>
  );
};

export default App;
